import { defineStore } from 'pinia';
import { apolloClient } from '@/utils/apollo';
import { useDepartmentStore } from '@/stores/department';
import constants from '@/utils/constants';
import { addMonths } from 'date-fns';
import { format } from '@/utils/date-fns';
import { meetingRangeQuery } from '@/graphql/queries/meetingRange.js';
import { meetingEntryQuery } from '@/graphql/queries/meetingEntry.js';
import { meetingSignupScreenMutation } from '@/graphql/mutations/meetingSignupScreen.js';
import { meetingCancelSignupScreenMutation } from '@/graphql/mutations/meetingCancelSignupScreen.js';
import { useProfilesStore } from '@/stores/profiles';
import { IDepartment, IImage, IInstitution, IProfile } from '@/interfaces';
import api from '@/api';
import { ScreenProfile } from '@/graphql/generated-types';

export interface IAgendaPoint {
  id: string;
  title: string;
  description: string;
  order: number;
}

export interface ISuggestion {
  id: string;
  suggestion: string;
  suggester: IProfile;
  title: string;
}

export interface IMeeting {
  agendaPoints: IAgendaPoint[];
  cancelled: boolean;
  category: string;
  coverImage: IImage;
  departments: IDepartment[];
  endDate: string | number | Date;
  id: string | null;
  institution: IInstitution | null;
  location: string;
  participants: IProfile[];
  repeat?: { rrule: string };
  signupPossible?: boolean;
  startDate: string | number | Date;
  suggestions?: ISuggestion[];
  title: string;
}

const DEFAULT_MEETING: IMeeting = {
  agendaPoints: [],
  cancelled: false,
  departments: [],
  category: '',
  coverImage: { source: '' },
  endDate: '',
  id: null,
  institution: null,
  location: '',
  participants: [],

  startDate: '',
  title: '',
};

export interface IMeetingState {
  all: IMeeting[];
  selected: IMeeting | null;
}

export const useMeetingsStore = defineStore('meetings', {
  state: (): IMeetingState => ({
    all: [],
    selected: DEFAULT_MEETING,
  }),
  actions: {
    async getMeetings() {
      const departmentStore = useDepartmentStore();

      if (!departmentStore.id) {
        throw new Error('No department id provided');
      }

      const today = new Date();

      await apolloClient
        .query({
          query: meetingRangeQuery,
          variables: {
            filter: {
              departments: departmentStore.id,
            },
            dateFrom: format(new Date(), 'yyyy-MM-dd'),
            dateTo: format(addMonths(today, 12), 'yyyy-MM-dd'),
            timezone: constants.timezone,
          },
          fetchPolicy: 'no-cache',
        })
        .then((response) => {
          this.all = response.data.meetingRange;
        })
        .catch((error) => {
          console.log('Error getting meetings', error);
        });
    },
    async getSelectedMeeting(meetingId: string) {
      this.selected = null;

      await apolloClient
        .query({
          query: meetingEntryQuery,
          variables: {
            id: meetingId,
          },
          fetchPolicy: 'no-cache',
        })
        .then((response) => {
          const meetingEntry = response.data.meetingEntry;

          const updatedResponse = {
            data: {
              meetingEntry: {
                ...meetingEntry,
                participants: meetingEntry.participants.map(
                  (participant: IProfile) => {
                    if (!participant.showImage)
                      participant.displayImage = constants.avatarPlaceholder;
                    return participant;
                  }
                ),
                suggestions: meetingEntry.suggestions.map(
                  (suggestion: ISuggestion) => {
                    if (!suggestion.suggester.showImage)
                      suggestion.suggester.displayImage =
                        constants.avatarPlaceholder;
                    return suggestion;
                  }
                ),
              },
            },
          };

          this.selected = updatedResponse.data.meetingEntry;
        })
        .catch((error) => {
          console.log('Could not get meeting entry: ', error);
        });
    },
    async meetingSignupScreen({
      subscription,
      profile,
      participating,
    }: {
      subscription: any;
      profile: IProfile;
      participating: boolean;
    }) {
      const profilesStore = useProfilesStore();

      const mutation = participating
        ? meetingSignupScreenMutation
        : meetingCancelSignupScreenMutation;

      await apolloClient
        .mutate({
          mutation: mutation,
          variables: {
            subscription,
            profile,
          },
        })
        .then(async () => {
          await profilesStore.setPinValidation({
            validating: false,
            validPin: true,
          });

          this.getSelectedMeeting(subscription.id);
        })
        .catch((error) => {
          profilesStore.setPinValidation({
            validating: false,
            validPin: false,
          });

          throw new Error('Error signing up to meeting', error);
        });
    },
    async meetingSuggest({
      profile,
      meetingId,
      suggestion,
    }: {
      profile: ScreenProfile;
      meetingId: string;
      suggestion: string;
    }) {
      await api.meetings
        .addSuggestion({ profile, meetingId, suggestion })
        .then(() => {
          this.getSelectedMeeting(meetingId);
        })
        .catch((error) => {
          throw new Error('Error saving suggestion', error);
        });
    },
  },
});
